import styled from 'styled-components';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { Container, Divider, IconName, Stack } from '@optimus-web/core';
import { lt, LocalizedText, showSuccessAlert } from '@mews/framework';
import { TextKey, RouteName, ActionType } from '@mews-commander/enums';
import { ModalWrapper } from '@mews-commander/ui';
import { push, useResolveRoute } from '@mews/routing';
import { WithGuideLink } from '@mews-commander/types';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { BackendPage } from '../../app';
import { AdditionalServiceMenu } from '../../appSidebar/Menus/AdditionalServiceMenu';

type ProductServiceDetailPageProps = WithGuideLink & {
    enterpriseId: string;
    productServiceId: string;
    additionalServicesName: string | undefined;
};

const StyledStack = styled(Stack)`
    height: 100%;
    overflow: auto;
`;

export const ProductServiceDetailPage = ({
    enterpriseId,
    productServiceId,
    guideLink,
    additionalServicesName,
}: ProductServiceDetailPageProps) => {
    const resolveRoute = useResolveRoute();
    const dispatch = useCommanderThunkDispatch();
    const enterprise = useCurrentEnterprise();

    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, {
        enterpriseId,
    });

    const serviceIndexBreadcrumbUrl = resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: enterpriseId } });

    const productServiceBackendUrl = resolveRoute(RouteName.ProductServiceDetail, {
        id: productServiceId,
    });

    const breadCrumbs: Breadcrumbs = [
        {
            text: enterprise.name,
            url: enterpriseUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.ServicePlural} />,
            url: serviceIndexBreadcrumbUrl,
        },
        {
            active: true,
            text: additionalServicesName ?? '',
        },
    ];

    const { mutateAsync: deleteProductService } = useRepositoryMutation('services', 'deleteProductService', {
        invalidateQueries: {
            services: ['getAll'],
        },
        onSuccess: () => {
            showSuccessAlert({
                title: lt('DeleteSuccessMessage', { entity: additionalServicesName ?? '' }),
            });
        },
    });

    const productServiceActions: HeadingAction[] = [
        {
            icon: IconName.Delete,
            actionId: 'delete',
            text: <LocalizedText textKey={TextKey.DeleteHint} />,
            title: (
                <LocalizedText
                    textKey={TextKey.DeleteConfirmation}
                    substitutes={{ entity: additionalServicesName || '' }}
                />
            ),
            buttonText: <LocalizedText textKey={TextKey.Delete} />,
            isDanger: true,
            onConfirmation: async () => {
                await deleteProductService({
                    serviceId: productServiceId,
                });

                dispatch(push(resolveRoute(RouteName.ServiceIndex, { query: { OwnerId: enterpriseId } })));
            },
            type: ActionType.ConfirmationDialog,
        },
        {
            url: guideLink,
            target: '_blank',
            icon: IconName.Help,
            text: lt('Help'),
            type: ActionType.Link,
            actionId: 'help',
        },
    ];

    return (
        <ModalWrapper>
            <ModalHeader breadcrumbs={breadCrumbs} actions={productServiceActions} />
            <StyledStack>
                <Container minWidth="360px">
                    <AdditionalServiceMenu productServiceId={productServiceId} />
                </Container>
                <Divider direction="vertical" />
                <div>
                    <Container padding={['300', '1000']}>
                        <BackendPage
                            loadingOverlayFixed
                            absolutePosition={false}
                            layout="EmptyDialog"
                            url={productServiceBackendUrl}
                        />
                    </Container>
                </div>
            </StyledStack>
        </ModalWrapper>
    );
};
